import { API_CONFIG } from '../config/api';
import { secureTokenManager } from '../utils/secureTokenManager';

// Service Provider interfaces
export interface ServiceProviderType {
  _id?: string;
  tag: string;
  name: string;
  description: string;
  icon: string;
}

export interface ServiceCategory {
  _id: string;
  tag: string;
  name: string;
  description: string;
  icon: string;
  sortOrder: number;
}

export interface ServiceOffering {
  serviceProviderType: ServiceProviderType;
  categories: ServiceCategory[];
}

export interface ServiceProvider {
  id?: string; // Optional since API might not return it
  fullName: string;
  contactNumber: string;
  address: string;
  businessAddress?: string; // API field for business address
  registeredOn: string;
  servicesOffered: number;
  status: number | 'enabled' | 'disabled' | 'blocked'; // API returns number (1 = enabled, 0 = disabled)
  serviceProviderType?: ServiceProviderType | null;
  categories?: ServiceCategory[];
  serviceOfferings?: ServiceOffering[] | null;
  servicesList?: string[];
  email?: string;
  rating?: number;
  overallRating?: number;
  ratingCount?: number;
  totalJobs?: number;
  completedJobs?: number;
  lastActive?: string;
  formattedAddress?: string;
  updatedAt?: string;
  documents?: {
    profilePhoto?: {
      url: string;
      uploadedAt: string;
    };
    businessIdCard?: {
      url: string;
      uploadedAt: string;
    };
    stateIdCardFront?: {
      url: string;
      uploadedAt: string;
    };
    stateIdCardBack?: {
      url: string;
      uploadedAt: string;
    };
  };
  // Device details from API as an object
  deviceInfo?: {
    fcmToken?: string;
    deviceType?: 'ios' | 'android';
    deviceId?: string;
    deviceModel?: string;
    osVersion?: string;
    appVersion?: string;
    lastActiveAt?: string;
    registeredAt?: string;
  };
  // Legacy fields for backward compatibility
  fcmToken?: string;
  deviceType?: 'ios' | 'android';
  deviceId?: string;
  deviceModel?: string;
  osVersion?: string;
  // Payment due fields
  firstServiceCompleted?: boolean;
  firstServiceCompletedAt?: string; // ISO date string
  firstServiceCompletedOn?: string; // Formatted date string
  paymentStatus?: 'completed' | 'pending' | 'required_after_first_service';
  tradeName?: string;
  specialization?: string;
  profileStatus?: string;
  verificationStatus?: string;
  isActive?: boolean;
  isAvailable?: boolean;
  lat?: string;
  lng?: string;
  serviceType?: string; // Simple string service type from payment due API
  // Plan details
  planId?: string;
  plan?: {
    planId: string;
    planName: string;
    amount: number;
    currency: string;
    features: string[];
    validity: number;
    description?: string;
    isActive: boolean;
  };
  planValidityUntil?: string;
  isPlanExpired?: boolean;
  paymentDetails?: {
    amount: number;
    currency: string;
    paymentDate: string;
    paymentMethod: string;
    membershipExpiry: string;
  };
}

export interface ServiceProviderListResponse {
  success: boolean;
  data: {
    serviceProviders: ServiceProvider[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
  message?: string;
}

type ProviderServiceSelection = {
  serviceProviderType?: ServiceProviderType | null;
  categories?: ServiceCategory[];
  serviceOfferings?: ServiceOffering[] | null;
  servicesOffered?: number;
};

export const getProviderServiceOfferings = (provider: ProviderServiceSelection): ServiceOffering[] => {
  // An explicit empty grouped selection must not resurrect the legacy primary service.
  if (Array.isArray(provider.serviceOfferings)) return provider.serviceOfferings;
  const categories = Array.isArray(provider.categories) ? provider.categories : [];
  if (!provider.serviceProviderType?.name && !provider.serviceProviderType?.tag && categories.length === 0) return [];
  return [{
    serviceProviderType: provider.serviceProviderType || { tag: '', name: '', description: '', icon: '' },
    categories,
  }];
};

export const getProviderServiceCount = (provider: ProviderServiceSelection): number => {
  if (typeof provider.servicesOffered === 'number' && Number.isFinite(provider.servicesOffered) && provider.servicesOffered >= 0) {
    return provider.servicesOffered;
  }
  return getProviderServiceOfferings(provider).reduce((count, offering) => count + offering.categories.length, 0);
};

const normalizeServiceProviderListItem = (provider: ServiceProvider): ServiceProvider => ({
  ...provider,
  categories: Array.isArray(provider.categories) ? provider.categories : [],
  servicesOffered: getProviderServiceCount(provider),
});

export interface ServiceProviderFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'all' | 'enabled' | 'disabled' | 'blocked';
  serviceProviderType?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface StatusUpdateRequest {
  mobileNumber: string;
  status: 'enabled' | 'disabled' | 'blocked' | 'unblocked';
  reason?: string;
}

export interface StatusUpdateResponse {
  success: boolean;
  message: string;
  data: {
    serviceProviderId: string;
    fullName: string;
    contactNumber: string;
    previousStatus: string;
    newStatus: string;
    updatedAt: string;
    reason?: string;
  };
}

// Service Provider Update interfaces
export interface UpdateServiceProviderRequest {
  contactNumber: string;
  serviceProviderType: string;
  categories: string[];
}

export interface UpdateServiceProviderResponse {
  success: boolean;
  message: string;
  data: {
    contactNumber: string;
    fullName: string;
    serviceProviderType: ServiceProviderType;
    categories: ServiceCategory[];
    updatedAt: string;
  };
}

// Pending Registration interfaces
export interface PendingRegistration {
  id: string;
  fullName: string;
  contactNumber: string;
  email: string;
  tradeName: string;
  serviceProviderType: ServiceProviderType;
  categories: ServiceCategory[];
  specialization: string;
  businessAddress: string;
  profileStatus: string;
  paymentStatus: 'completed' | 'pending';
  verificationStatus: 'pending' | 'verified' | 'rejected';
  documents: {
    profilePhoto: {
      url: string;
      uploadedAt: string;
    };
    businessIdCard: {
      url: string;
      uploadedAt: string;
    };
    stateIdCardFront: {
      url: string;
      uploadedAt: string;
    };
    stateIdCardBack: {
      url: string;
      uploadedAt: string;
    };
  };
  registeredOn: string;
  servicesOffered: number;
}

export interface PendingRegistrationsResponse {
  success: boolean;
  data: {
    serviceProviders: PendingRegistration[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    filters: {
      serviceProviderType: string | null;
      search: string | null;
    };
    description: string;
  };
}

export interface PendingRegistrationsFilters {
  page?: number;
  limit?: number;
  serviceProviderType?: string;
}

export interface PaymentDueServiceProvidersFilters {
  page?: number;
  limit?: number;
  serviceProviderType?: string;
  search?: string;
}

export interface UpdateServiceProviderStatusRequest {
  mobileNumber: string;
  status: 'activate' | 'reject';
  reason?: string;
}

export interface UpdateServiceProviderStatusResponse {
  success: boolean;
  message: string;
  data?: any;
}

export interface ServiceProviderProfile {
  id: string;
  fullName: string;
  contactNumber: string;
  tradeName: string;
  email: string;
  businessAddress: string;
  formattedAddress?: string;
  serviceProviderType: ServiceProviderType;
  categories: ServiceCategory[];
  serviceOfferings?: ServiceOffering[] | null;
  specialization: string;
  profileStatus: string;
  paymentStatus: 'completed' | 'pending';
  verificationStatus?: 'pending' | 'verified' | 'rejected';
  status: number | 'enabled' | 'disabled' | 'blocked';
  isActive: boolean;
  isAvailable?: boolean;
  overallRating?: number;
  ratingCount?: number;
  registeredOn: string;
  updatedAt?: string;
  servicesOffered?: number;
  device?: {
    fcmToken?: string;
    deviceType?: 'ios' | 'android';
    deviceId?: string;
    deviceModel?: string;
    osVersion?: string;
    appVersion?: string;
    lastActiveAt?: string;
    registeredAt?: string;
  };
  planId?: string;
  plan?: {
    planId: string;
    planName: string;
    amount: number;
    currency: string;
    features: string[];
    validity: number;
    description?: string;
    isActive: boolean;
  };
  planValidityUntil?: string;
  isPlanExpired?: boolean;
  paymentDetails?: {
    amount: number;
    currency: string;
    paymentDate: string;
    paymentMethod: string;
    membershipExpiry: string;
  };
  documents: {
    profilePhoto: {
      url: string;
      uploadedAt: string;
    };
    businessIdCard: {
      url: string;
      uploadedAt: string;
    };
    stateIdCardFront: {
      url: string;
      uploadedAt: string;
    };
    stateIdCardBack: {
      url: string;
      uploadedAt: string;
    };
  };
}

export interface ServiceProviderProfileResponse {
  success: boolean;
  data: {
    serviceProvider: ServiceProviderProfile;
  };
  message?: string;
}

export interface TestNotificationRequest {
  fcmToken: string;
  deviceType?: 'ios' | 'android';
  title?: string;
  body?: string;
  data?: Record<string, any>;
}

export interface TestNotificationResponse {
  success: boolean;
  message: string;
  data?: {
    messageId: string;
    fcmToken: string;
    deviceType: 'ios' | 'android';
    notification: {
      title: string;
      body: string;
    };
    sentAt: string;
  };
  error?: string;
  details?: string;
}

export class ServiceProviderService {
  private baseURL: string;
  private requestQueue: Array<() => Promise<any>> = [];
  private isProcessingQueue: boolean = false;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  // Utility method to check if error is rate limiting
  private isRateLimitError(error: any): boolean {
    if (error instanceof Error) {
      return error.message.includes('429') || 
             error.message.includes('Rate limit') || 
             error.message.includes('Too Many Requests');
    }
    return false;
  }

  // Utility method to get retry delay from error message or headers
  private getRetryDelay(error: any, retryCount: number): number {
    const baseDelay = 1000; // 1 second
    
    // Check if error message contains retry information
    if (error instanceof Error && error.message.includes('retry after')) {
      const match = error.message.match(/retry after (\d+)/i);
      if (match) {
        return parseInt(match[1]) * 1000;
      }
    }
    
    // Use exponential backoff
    return baseDelay * Math.pow(2, retryCount);
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0
  ): Promise<T> {
    // Remove /api prefix from endpoint if baseURL already includes it
    let normalizedEndpoint = endpoint;
    if (this.baseURL.endsWith('/api') && endpoint.startsWith('/api/')) {
      normalizedEndpoint = endpoint.substring(4); // Remove '/api' prefix
    } else if (this.baseURL === '/api' && endpoint.startsWith('/api')) {
      normalizedEndpoint = endpoint.startsWith('/api/') ? endpoint.substring(4) : endpoint.substring(3);
    }
    const url = `${this.baseURL}${normalizedEndpoint}`;
    const maxRetries = 3;
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
        // Cache-busting headers to prevent browser caching
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        // Remove Expires header to avoid CORS issues
        ...options.headers,
      },
    };

    // Debug logging removed for security

    try {
      const response = await fetch(url, config);
      
      // Response logging removed for security
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Error logging removed for security
        
        // Handle 429 Rate Limiting with retry logic
        if (response.status === 429 && retryCount < maxRetries) {
          const retryAfter = response.headers.get('Retry-After');
          const delay = retryAfter 
            ? parseInt(retryAfter) * 1000 
            : this.getRetryDelay(errorData, retryCount);
          
          // Rate limit logging removed for security
          
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.makeRequest<T>(endpoint, options, retryCount + 1);
        }
        
        // Handle other errors or max retries reached
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const suggestedDelay = retryAfter ? `${retryAfter} seconds` : 'a few moments';
          throw new Error(
            `Rate limit exceeded. Please wait ${suggestedDelay} before trying again. ${errorData.message || ''}`
          );
        }
        
        // Try to extract detailed error message
        const errorMessage = errorData.error || errorData.message || errorData.details || `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).errorData = errorData;
        throw error;
      }

      const data = await response.json();
      // Success logging removed for security
      return data;
    } catch (error) {
      console.error('💥 API Request Failed:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Get list of service providers with pagination
  async getServiceProviders(filters: ServiceProviderFilters = {}): Promise<ServiceProviderListResponse> {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    // Add search parameter
    if (filters.search) params.append('search', filters.search);
    
    // Add status filter
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
      // Filter logging removed for security
    } else {
      // Filter logging removed for security
    }
    
    // Add service provider type filter
    if (filters.serviceProviderType) {
      params.append('serviceProviderType', filters.serviceProviderType);
    }
    
    // Add sorting parameters only if explicitly set
    if (filters.sortBy && filters.sortBy !== null) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder && filters.sortBy && filters.sortBy !== null) params.append('sortOrder', filters.sortOrder);
    
    // Add cache-busting timestamp to prevent browser caching (commented out to match API expectations)
    // params.append('_t', Date.now().toString());

    const queryString = params.toString();
    const endpoint = `/api/service-providers${queryString ? `?${queryString}` : ''}`;
    
    // Endpoint logging removed for security
    const response = await this.makeRequest<ServiceProviderListResponse>(endpoint);

    if (!response.success || !response.data || !Array.isArray(response.data.serviceProviders)) {
      return response;
    }

    return {
      ...response,
      data: {
        ...response.data,
        serviceProviders: response.data.serviceProviders.map(normalizeServiceProviderListItem),
      },
    };
  }

  // Get single service provider by ID
  async getServiceProvider(id: string): Promise<ServiceProvider> {
    const endpoint = `/api/service-providers/${id}?_t=${Date.now()}`;
    const response = await this.makeRequest<{ data: ServiceProvider; success: boolean }>(endpoint);
    return response.data;
  }

  // Get service provider by contact number (for refreshing after update)
  async getServiceProviderByContactNumber(contactNumber: string): Promise<ServiceProvider | null> {
    try {
      // Search logging removed for security
      // Get all service providers and find the one with matching contact number
      const response = await this.getServiceProviders({ limit: 1000 }); // Get a large number to find the specific one
      const serviceProvider = response.data.serviceProviders.find(sp => sp.contactNumber === contactNumber);
      
      if (serviceProvider) {
        // Found logging removed for security
      } else {
        // Warning logging removed for security
      }
      
      return serviceProvider || null;
    } catch (error) {
      console.error('❌ Error fetching service provider by contact number:', error);
      return null;
    }
  }

  // Update service provider status
  async updateServiceProviderStatus(mobileNumber: string, status: 'enabled' | 'disabled' | 'blocked' | 'unblocked', reason?: string): Promise<StatusUpdateResponse> {
    const requestBody: StatusUpdateRequest = {
      mobileNumber,
      status,
      reason
    };
    
    // Status update logging removed for security
    
    // Debug logging removed for security
    
    return this.makeRequest<StatusUpdateResponse>('/api/admin/service-providers/status', {
      method: 'PUT',
      body: JSON.stringify(requestBody),
    });
  }

  // Update service provider details (type and categories)
  async updateServiceProvider(contactNumber: string, serviceProviderType: string, categories: string[]): Promise<UpdateServiceProviderResponse> {
    const requestBody: UpdateServiceProviderRequest = {
      contactNumber,
      serviceProviderType,
      categories
    };
    
    // Service provider update logging removed for security
    
    // Debug info logging removed for security
    
    // Make request with the current admin session token
    const url = `${this.baseURL}/api/service-providers/update`;
    const config: RequestInit = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
      },
      body: JSON.stringify(requestBody),
    };

    // API request logging removed for security

    try {
      const response = await fetch(url, config);
      
      // Response logging removed for security
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Error logging removed for security
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      // Success logging removed for security
      return data;
    } catch (error) {
      console.error('💥 API Request Failed:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Get service provider statistics
  async getServiceProviderStats(): Promise<{
    total: number;
    enabled: number;
    disabled: number;
    blocked: number;
    monthToDate: number;
    yearToDate: number;
  }> {
    const endpoint = `/api/service-providers/stats?_t=${Date.now()}`;
    const response = await this.makeRequest<{
      data: {
        total: number;
        enabled: number;
        disabled: number;
        blocked: number;
        monthToDate: number;
        yearToDate: number;
      };
      success: boolean;
    }>(endpoint);
    
    return response.data;
  }

  // Get pending registrations (service providers who haven't completed payment)
  async getPendingRegistrations(filters: PendingRegistrationsFilters = {}): Promise<PendingRegistrationsResponse> {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    // Add service provider type filter
    if (filters.serviceProviderType) {
      params.append('serviceProviderType', filters.serviceProviderType);
    }
    
    // Add cache-busting timestamp to prevent browser caching (commented out to match API expectations)
    // params.append('_t', Date.now().toString());

    const queryString = params.toString();
    const endpoint = `/api/service-providers/pending-verification${queryString ? `?${queryString}` : ''}`;
    
    // Endpoint logging removed for security
    return this.makeRequest<PendingRegistrationsResponse>(endpoint);
  }

  // Get payment due service providers (completed first service, payment pending)
  async getPaymentDueServiceProviders(filters: PaymentDueServiceProvidersFilters = {}): Promise<ServiceProviderListResponse> {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    // Add search parameter
    if (filters.search) params.append('search', filters.search);
    
    // Add service provider type filter
    if (filters.serviceProviderType) {
      params.append('serviceProviderType', filters.serviceProviderType);
    }

    const queryString = params.toString();
    const endpoint = `/api/service-providers/pending-payment-after-first-service${queryString ? `?${queryString}` : ''}`;
    
    return this.makeRequest<ServiceProviderListResponse>(endpoint);
  }

  // Update pending service provider status (approve/reject)
  async updatePendingServiceProviderStatus(request: UpdateServiceProviderStatusRequest): Promise<UpdateServiceProviderStatusResponse> {
    // Status update logging removed for security
    
    const endpoint = '/api/service-providers/status';
    return this.makeRequest<UpdateServiceProviderStatusResponse>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(request),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Get service provider profile details by contact number
  async getServiceProviderProfile(contactNumber: string): Promise<ServiceProviderProfileResponse> {
    // Profile fetch logging removed for security
    
    const endpoint = `/api/service-providers/profile/${encodeURIComponent(contactNumber)}`;
    return this.makeRequest<ServiceProviderProfileResponse>(endpoint);
  }

  // Test API connection
  async testConnection(): Promise<boolean> {
    try {
      // Connection test logging removed for security
      return true;
    } catch (error) {
      console.error('❌ API connection failed:', error);
      return false;
    }
  }

  // Test status update endpoint specifically
  async testStatusUpdateEndpoint(mobileNumber: string): Promise<boolean> {
    try {
      // Status update test logging removed for security
      const testData: StatusUpdateRequest = {
        mobileNumber,
        status: 'enabled',
        reason: 'Test connection'
      };
      
      // This will likely fail with 404, but we'll see the exact URL being called
      await this.makeRequest<StatusUpdateResponse>('/api/admin/service-providers/status', {
        method: 'PUT',
        body: JSON.stringify(testData),
      });
      
      // Status update success logging removed for security
      return true;
    } catch (error) {
      console.error('❌ Status update endpoint test failed:', error);
      return false;
    }
  }

  // Test get service provider endpoint
  async testGetServiceProviderEndpoint(id: string): Promise<boolean> {
    try {
      // Get service provider test logging removed for security
      
      // This will likely fail with 404, but we'll see the exact URL being called
      await this.makeRequest<{ data: ServiceProvider; success: boolean }>(`/api/admin/service-providers/${id}`);
      
      // Get service provider success logging removed for security
      return true;
    } catch (error) {
      console.error('❌ Get service provider endpoint test failed:', error);
      return false;
    }
  }

  // Test authentication with the update endpoint
  async testUpdateEndpointAuthentication(): Promise<boolean> {
    try {
      // Authentication test logging removed for security
      
      const testData = {
        contactNumber: '+1234567890',
        serviceProviderType: 'test_type',
        categories: ['test_category']
      };
      
      // Test request details logging removed for security
      
      // This will likely fail, but we'll see the exact error
      await this.makeRequest<UpdateServiceProviderResponse>('/api/service-providers/update', {
        method: 'PUT',
        body: JSON.stringify(testData),
      });
      
      // Authentication success logging removed for security
      return true;
    } catch (error) {
      console.error('❌ Update endpoint authentication test failed:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      }
      return false;
    }
  }

  // Send test notification to a device
  async sendTestNotification(request: TestNotificationRequest): Promise<TestNotificationResponse> {
    const endpoint = '/api/notifications/test';
    
    // Build request body, only including defined fields
    const requestBody: {
      fcmToken: string;
      deviceType?: 'ios' | 'android';
      title?: string;
      body?: string;
      data?: Record<string, any>;
    } = {
      fcmToken: request.fcmToken,
    };
    
    if (request.deviceType) {
      requestBody.deviceType = request.deviceType;
    }
    
    if (request.title) {
      requestBody.title = request.title;
    }
    
    if (request.body) {
      requestBody.body = request.body;
    }
    
    if (request.data && Object.keys(request.data).length > 0) {
      requestBody.data = request.data;
    }
    
    return this.makeRequest<TestNotificationResponse>(endpoint, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }
}

// Create singleton instance
export const serviceProviderService = new ServiceProviderService();
// Make test method available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).testServiceProviderAuth = () => {
    // Authentication test logging removed for security
    return serviceProviderService.testUpdateEndpointAuthentication();
  };
  
  (window as any).serviceProviderService = serviceProviderService;
}
